import { IFileDto } from '../../../config/fileConfig'
import * as React from 'react'
import { FunctionComponent, PropsWithChildren, ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { TaskMutationDto } from '../../../config/taskConfig'
import { useDispatch } from 'react-redux'
import { Formik, useFormikContext } from 'formik'
import Form, { EntityFormProps, EntityParams } from '../Form'
import * as Yup from 'yup'
import { Schema } from 'yup'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { formatFullDate, formatIsoDate } from '../../../helpers/dateHelper'
import useUser from '../../../hooks/useUser'
import useAxios from '../../../hooks/useAxios'
import { flatMapDriveItemsToOverviewItemDto } from '../../../hooks/file/useFileDocuments'
import useRoles, { rolesWriteAllTask } from '../../../hooks/useRoles'
import FormContainer, { useFormContainerStyles } from '../../../components/Form/FormContainer'
import { useTranslation } from 'react-i18next'
import FormikTextareaField from '../../Formik/FormikTextareaField'
import FormikField from '../../Formik/FormikField'
import LocalDatePicker, { LocalDatePickerProps } from '../../DatePicker/LocalDatePicker'
import UserCombobox from '../../Combobox/UserCombobox'
import FileCombobox from '../../Combobox/FileCombobox'
import FormikComboboxField from '../../Formik/FormikComboboxField'
import FormikInputField from '../../Formik/FormikInputField'
import { SubComboboxProps } from '../../Combobox/BaseCombobox'
import { UserListItemDto } from '../../../config/userConfig'
import { FileListItemDto } from '../../../context/FileContext'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Button,
    Divider,
    Field,
    makeStyles,
    Text,
    tokens,
    Tooltip,
} from '@fluentui/react-components'
import { outlookDisplayNewMessageForm } from '../../../helpers/outlookHelper'
import useUsers from '../../../hooks/useUsers'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import { bundleIcon, MailFilled, MailRegular } from '@fluentui/react-icons'
import TaskStatusCombobox, { TaskStatusComboboxProps } from '../../Combobox/i18n/TaskStatusCombobox'
import useTaskStatuses from '../../../hooks/useTaskStatuses'
import useTasks from '../../../hooks/useTasks'
import EnumMapCombobox, { EnumMapComboboxProps } from '../../Combobox/EnumMapCombobox'
import FormikDurationField from '../../Formik/FormikDurationField'
import Dropzone from '../../Dropzone/Dropzone'
import {
    driveItemListProperties,
    driveItemOverviewProperties,
    findFolderInTree,
    mapDriveItemToOverviewItemDto,
    uploadDocument,
} from '../../../helpers/documentHelper'
import { FileDocumentAction } from '../../../config/fileDocumentConfig'
import { useSettings } from '../../../hooks/useSettings'
import { DocumentOverviewItemDto, DocumentSortOrder, FolderType, IFolderOverviewItemDto, IRootOverviewItemDto } from '../../../config/documentConfig'
import DocumentFlatTree, { DocumentSelectionStrategy } from '../../Document/DocumentFlatTree'
import { Stack, StackItem } from '@fluentui/react'
import DocumentLink from '../../Document/DocumentLink'
import { usePrevious } from '@fluentui/react-hooks'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import { Destination } from '../../../context/AxiosContext'
import { AxiosResponse } from 'axios'

export const useStyles = makeStyles({
    //Move this container up when all pages have been updated.
    pageContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: tokens.spacingHorizontalS,
    },
})

const MailIcon = bundleIcon(MailFilled, MailRegular)

type Props = EntityFormProps<TaskMutationDto, TaskMutationDto>

const getValidationSchema = (hasCategorySetting: boolean, translate: (key: string) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        category: hasCategorySetting
            ? Yup.string().label(translate('task.category.label')).required()
            : Yup.string().label(translate('task.category.label')).nullable(),
        title: Yup.string().required().label(translate('task.title')),
        description: Yup.string().nullable().label(translate('task.description')),
        start: Yup.string().notRequired().label(translate('task.start')),
        due: Yup.string()
            .required()
            .label(translate('task.due'))
            .test('is-valid-date-range', translate('task.edit.form.due.error.daterange'), (value, context) => {
                return !context.parent.start || new Date(value) >= new Date(context.parent.start)
            }),
        assigneeId: Yup.string().uuid().required().label(translate('task.assigneeid')),
        assignerId: Yup.string().uuid().required().label(translate('task.assignerid')),
        fileId: Yup.string().uuid().required().label(translate('task.fileid')),
        attachmentIds: Yup.array(),
        status: Yup.string().required().label(translate('task.status.label')),
    })
}

const Container: FunctionComponent<{ currentFile: IFileDto; returnUrl?: string }> = props => {
    const axios = useAxios()
    const dispatch = useDispatch()
    const history = useHistory()
    const { id } = useParams<EntityParams>()
    const roles = useRoles()
    const styles = useStyles()
    const formContainerStyles = useFormContainerStyles()
    const { settings } = useSettings()
    const { loaded: categoriesLoaded, categories: taskCategories, defaultCategory: defaultTaskCategory, expectedTime: useExpectedTime } = useTasks()
    const { taskStatuses, getStatusName } = useTaskStatuses()
    const { t: translate } = useTranslation()
    const { currentUser } = useUser()
    const { getUserById } = useUsers()
    const { viewport, isViewport } = useViewport()

    const taskCategoryOptions = useMemo<Map<string, string>>(
        () => new Map(taskCategories?.map(x => [x.key, x.name] as [string, string])),
        [categoriesLoaded, taskCategories],
    )

    const orientation = 'horizontal'
    const [accordionOpenItems, setAccordionOpenItems] = useState<string[]>([])
    const smallMenu = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])

    const { initialValues, values, setFieldValue } = useFormikContext<TaskMutationDto>()
    const [selectedFile, setSelectedFile] = useState<IFileDto>(props.currentFile)
    const previousFile = usePrevious(selectedFile)
    const [attachmentFolder, setAttachmentFolder] = useState<IFolderOverviewItemDto>()
    const [attachmentIds, setAttachmentIds] = useState<string[]>(initialValues.attachmentIds || [])
    const [attachments, setAttachments] = useState<DocumentOverviewItemDto[]>()

    // Keep refs in sync to avoid stale values in callbacks
    const selectedFileRef = useRef<IFileDto>(selectedFile)
    const attachmentFolderRef = useRef<IFolderOverviewItemDto>(attachmentFolder)

    useEffect(() => {
        selectedFileRef.current = selectedFile
    }, [selectedFile])

    useEffect(() => {
        attachmentFolderRef.current = attachmentFolder
    }, [attachmentFolder])

    useEffect(() => {
        // This is not the initial state.
        if (!values.fileId || values.fileId !== selectedFile?.id) {
            setAttachmentIds(initialValues.fileId === values.fileId ? initialValues.attachmentIds : [])

            if (!values.fileId) {
                setSelectedFile(undefined)
            } else if (values.fileId === props.currentFile?.id) {
                setSelectedFile(props.currentFile)
            } else {
                axios.get<IFileDto>(`/file/${values.fileId}`).then(result => setSelectedFile(result.data))
            }
        }
    }, [values.fileId])

    useEffect(() => {
        setFieldValue('attachmentIds', attachmentIds)
    }, [attachmentIds])

    useEffect(() => {
        if (selectedFile && selectedFile.id !== previousFile?.id) {
            axios
                .get<DriveItem>(`/items/${selectedFile.documentRootId}`, {
                    baseURL: Destination.Documents,
                    params: { $select: driveItemListProperties.join(','), $expand: 'children' },
                    headers: { SuppressErrors: 'true' },
                })
                .then(response => mapDriveItemToOverviewItemDto(response.data) as IRootOverviewItemDto)
                .then(response => findFolderInTree(response, FolderType.Attachments, settings?.folders, true))
                .then(response => setAttachmentFolder(response))

            Promise.all(
                (values.attachmentIds || []).flatMap(attachmentId =>
                    axios
                        .get<DriveItem>(`/items/${attachmentId}`, {
                            baseURL: Destination.Documents,
                            params: { $select: driveItemOverviewProperties.join(',') },
                            headers: { SuppressErrors: 'true' },
                        })
                        .then(response => (response.data?.id ? mapDriveItemToOverviewItemDto(response.data) : undefined)),
                ),
            ).then(attachment => setAttachments(attachment.filter(Boolean)))
        }
    }, [selectedFile])

    useEffect(() => {
        if (!id && defaultTaskCategory) {
            setFieldValue('category', defaultTaskCategory?.key)
        }
    }, [defaultTaskCategory])

    const setStatusValue = (status: string) => {
        return setFieldValue('status', status)
            .then(() => axios.put(`/task/${id}`, { ...values, status: status }))
            .then(() => (props.returnUrl ? history.push(props.returnUrl) : history.goBack()))
    }

    const onDropAccepted = (files: File[]): void => {
        if (!attachmentFolderRef.current?.id) {
            return
        }

        Promise.all((files || []).map(file => uploadDocument(axios, dispatch, attachmentFolderRef.current?.id, file)))
            .then((driveItems: AxiosResponse<DriveItem>[]) => {
                const newItems = driveItems.map(driveItem => mapDriveItemToOverviewItemDto(driveItem.data))
                setAttachments(prevState => {
                    const merged = [...(prevState || []), ...newItems]
                    return Array.from(new Map(merged.map(it => [it.id, it])).values())
                })
                setAttachmentIds(prevState => {
                    const newIds = driveItems.map(driveItem => driveItem.data?.id).filter(Boolean)
                    const merged = [...(prevState || []), ...newIds]
                    return [...new Set(merged)]
                })
            })
            .then(() => {
                dispatch({ type: FileDocumentAction.ResetOverview })
            })
    }

    return (
        <Dropzone
            disabled={!attachmentFolder || values.fileId === undefined}
            tipKey={'task.attachments.upload'}
            onDropAccepted={onDropAccepted}
            dropZoneClassname={formContainerStyles.body}
        >
            {id && (
                <div className={styles.pageContainer}>
                    <div className={styles.pageContainer}>
                        {taskStatuses.map((task, index) => (
                            <Button key={index} onClick={() => setStatusValue(task.alpha)} disabled={values.status === task.alpha}>
                                {getStatusName(task.alpha)}
                            </Button>
                        ))}
                    </div>
                    <Tooltip content={translate('task.edit.email.action')} relationship={'description'}>
                        <Button
                            icon={smallMenu ? <MailIcon /> : undefined}
                            disabled={values.attachmentIds?.length > 0 && attachments === undefined}
                            onClick={() =>
                                outlookDisplayNewMessageForm({
                                    toRecipients: [values.assigneeId]
                                        .filter(userId => userId !== currentUser?.id)
                                        .map(userId => getUserById(userId))
                                        .map(user => ({
                                            emailAddress: user.email?.trim(),
                                            displayName: user.fullName,
                                        })) as Office.EmailAddressDetails[],
                                    subject: `${values.title} [${selectedFile?.reference}]`,
                                    htmlBody: translate('task.edit.email.body', {
                                        link: window.location.origin + window.location.pathname + window.location.hash,
                                        title: values.title?.replaceAll('<', '[').replaceAll('>', ']'),
                                        description: values.description?.replaceAll('<', '[').replaceAll('>', ']'),
                                        startDate: formatFullDate(values.start),
                                        dueDate: formatFullDate(values.due),
                                        assigneeName: getUserById(values.assigneeId)?.fullName,
                                        assignerName: getUserById(values.assignerId)?.fullName,
                                        file: selectedFile.reference,
                                        workflowStep: values.workflowStepName,
                                        attachments: flatMapDriveItemsToOverviewItemDto(attachments || [])
                                            ?.filter(item => (values.attachmentIds || []).includes(item.id))
                                            .map(item => {
                                                return translate('task.edit.email.attachment', {
                                                    name: item.name,
                                                    link: item.url,
                                                })
                                            }),
                                        interpolation: { escapeValue: false },
                                    }),
                                })
                            }
                        >
                            {!smallMenu && translate('task.edit.email.action')}
                        </Button>
                    </Tooltip>
                </div>
            )}
            {taskCategories?.length > 0 && (
                <FormikComboboxField<TaskMutationDto, EnumMapComboboxProps<string>>
                    orientation={orientation}
                    required
                    labelKey={'task.category.label'}
                    fieldName={'category'}
                    component={EnumMapCombobox<string>}
                    componentProps={{
                        values: taskCategoryOptions,
                    }}
                />
            )}
            <FormikInputField<TaskMutationDto>
                orientation={orientation}
                required
                labelKey={'task.title'}
                fieldName={'title'}
                componentProps={{
                    autoFocus: true,
                }}
            />
            <FormikTextareaField<TaskMutationDto>
                orientation={orientation}
                labelKey={'task.description'}
                fieldName={'description'}
                componentProps={{
                    rows: 5,
                }}
            />
            {taskStatuses?.length > 0 && (
                <FormikComboboxField<TaskMutationDto, Partial<TaskStatusComboboxProps>>
                    orientation={orientation}
                    labelKey={'task.filter.status.label'}
                    fieldName={'status'}
                    component={TaskStatusCombobox}
                />
            )}
            <FormikField<LocalDatePickerProps, TaskMutationDto>
                orientation={orientation}
                labelKey={'task.start'}
                fieldName={'start'}
                component={LocalDatePicker}
                componentProps={{
                    isoValue: values.start,
                    maxDate: values.due ? new Date(values.due) : undefined,
                    onSelectDate: date => setFieldValue('start', date),
                }}
            />
            <FormikField<LocalDatePickerProps, TaskMutationDto>
                orientation={orientation}
                required
                labelKey={'task.due'}
                fieldName={'due'}
                component={LocalDatePicker}
                componentProps={{
                    isoValue: values.due,
                    minDate: values.start ? new Date(values.start) : undefined,
                    onSelectDate: date => setFieldValue('due', date),
                }}
            />
            {useExpectedTime && <FormikDurationField<TaskMutationDto> orientation={orientation} labelKey={'task.duration'} fieldName={'duration'} />}
            <FormikComboboxField<TaskMutationDto, Partial<SubComboboxProps<UserListItemDto>>>
                orientation={orientation}
                required
                labelKey={'task.assigneeid'}
                fieldName={'assigneeId'}
                component={UserCombobox}
                componentProps={{ tabIndex: -1 }}
            />
            <FormikComboboxField<TaskMutationDto, Partial<SubComboboxProps<UserListItemDto>>>
                orientation={orientation}
                required
                labelKey={'task.assignerid'}
                fieldName={'assignerId'}
                component={UserCombobox}
                componentProps={{ readOnly: !roles.any(rolesWriteAllTask), tabIndex: -1 }}
            />
            <FormikComboboxField<TaskMutationDto, Partial<SubComboboxProps<FileListItemDto>>>
                orientation={orientation}
                required
                labelKey={'base.filepicker.label'}
                fieldName={'fileId'}
                component={FileCombobox}
                componentProps={{
                    tabIndex: !!props.currentFile ? -1 : undefined,
                }}
            />
            {values.workflowStepName && (
                <FormikInputField<TaskMutationDto>
                    orientation={orientation}
                    labelKey={'task.workflowstep'}
                    fieldName={'workflowStepName'}
                    componentProps={{
                        readOnly: true,
                    }}
                />
            )}

            <Field label={translate('task.attachmentids.label')} orientation={orientation}>
                {values.fileId && attachments === undefined && <Text>{translate('task.attachmentids.loading')}</Text>}
                {attachments !== undefined && attachments.length === 0 && <Text>{translate('task.attachmentids.empty')}</Text>}
                {attachments !== undefined && values.attachmentIds?.length > 0 && (
                    <Stack>
                        {values.attachmentIds.map(attachmentId => (
                            <StackItem key={attachmentId}>
                                <DocumentLink id={attachmentId} document={attachments.find(attachment => attachment.id === attachmentId)} />
                            </StackItem>
                        ))}
                    </Stack>
                )}
            </Field>
            {selectedFile && (
                <>
                    <Divider />
                    <Accordion<string>
                        openItems={accordionOpenItems}
                        onToggle={(_event, data) => {
                            setAccordionOpenItems(data.openItems)
                        }}
                        collapsible
                    >
                        <AccordionItem value={'attachments'}>
                            <AccordionHeader size="large">
                                {translate(accordionOpenItems.length > 0 ? 'task.attachmentids.selection' : 'task.attachmentids.selection.open')}
                            </AccordionHeader>
                            <AccordionPanel>
                                {selectedFile && (
                                    <DocumentFlatTree
                                        rootId={selectedFile.documentRootId}
                                        selectionStrategy={DocumentSelectionStrategy.HierarchyBackTrack}
                                        selectedOptions={values.attachmentIds}
                                        onOptionsSelect={values => setFieldValue('attachmentIds', values)}
                                        sortOrder={DocumentSortOrder.NameAsc}
                                    />
                                )}
                            </AccordionPanel>
                        </AccordionItem>
                    </Accordion>
                </>
            )}
        </Dropzone>
    )
}

export default function TaskForm(props: PropsWithChildren<Props>): ReactElement {
    const { currentUser } = useUser()
    const { loaded: categoriesLoaded, categories: taskCategories } = useTasks()
    const { t: translate } = useTranslation()
    const { getStartStatus } = useTaskStatuses()
    const location = useLocation()

    const [validationSchema, setValidationSchema] = useState<Schema>()

    useEffect(() => {
        setValidationSchema(getValidationSchema(taskCategories?.length > 0, translate))
    }, [categoriesLoaded, taskCategories])

    return (
        <Formik<TaskMutationDto>
            initialValues={{
                ...(props.entity?.data || {
                    status: getStartStatus()?.alpha,
                    title: '',
                    start: formatIsoDate(new Date()),
                    due: formatIsoDate(new Date()),
                    assigneeId: currentUser?.id,
                    assignerId: currentUser?.id,
                    fileId: props.currentFile?.id ?? props.initialValues?.fileId,
                    highlight: false,
                    today: false,
                    week: false,
                }),
                attachmentIds: props.entity?.data.attachmentIds || [],
                // Add any props that have been passed in the current request.
                ...(location.state as Record<string, unknown>),
            }}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={validationSchema}
            onSubmit={props.onSubmit}
        >
            <Form>
                <FormContainer>
                    <Container currentFile={props.currentFile} returnUrl={props.returnUrl} />
                    {props.children}
                </FormContainer>
            </Form>
        </Formik>
    )
}
